import {
  Box,
  CheckIcon,
  FormControlLabel,
  GKButton,
  GKCheckbox,
  GKModal,
  GKTextField,
  Grid2,
  SearchIcon,
  Show,
  Typography,
} from "components";
import { CompaniesListInterface } from "interfaces/PrivateRouteInterface";
import { filter, map } from "lodash";
import debounce from "lodash/debounce";
import React, { useEffect, useRef, useState } from "react";
import { useAppSelector } from "store/store";
import { GKModalProps } from "utils/commonInterface";

interface Props {
  onSuccess: (selectedCompanies: CompaniesListInterface[]) => void;
  selectedCompanies: CompaniesListInterface[];
  modelTitle: string;
}

const BATCH_SIZE = 50;

const CompanyModel = (props: GKModalProps & Props) => {
  const { open, setOpen, onSuccess, selectedCompanies, modelTitle } = props;
  const companies = useAppSelector((state) => state?.app?.companies || []);

  const [searchText, setSearchText] = useState("");
  const [visibleCount, setVisibleCount] = useState(BATCH_SIZE);
  const [selectCompany, setSelectCompany] = useState(selectedCompanies || []);

  const listRef = useRef<HTMLDivElement>(null);

  const filteredCompanies = filter(companies, (company: any) => {
    const search = searchText.toLowerCase();
    return (
      company?.name?.toLowerCase().includes(search) ||
      company?.isin?.toLowerCase().includes(search) ||
      company?.nse?.toLowerCase().includes(search) ||
      company?.bse?.toLowerCase().includes(search)
    );
  });

  const visibleCompanies = filteredCompanies.slice(0, visibleCount);

  useEffect(() => {
    setVisibleCount(BATCH_SIZE);
  }, [searchText, open]);

  const handleScroll = () => {
    const container = listRef.current;
    if (!container) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    if (scrollTop + clientHeight >= scrollHeight - 20) {
      setVisibleCount((prev) =>
        Math.min(prev + BATCH_SIZE, filteredCompanies.length),
      );
    }
  };

  const debouncedSearch = debounce((value: string) => {
    setSearchText(value);
  }, 300);

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    debouncedSearch(e.target.value);
  };

  const formattedCompanyNames = (() => {
    const names = selectCompany.map(
      (c: { companyName: string }) => c.companyName,
    );

    if (names?.length < 2) return names?.join(", ");
    return `${names[0].slice(0, 20)}, ${names[1].slice(0, 20)}...`;
  })();

  return (
    <GKModal
      modalTitle={modelTitle}
      subTitle={
        <Typography fontSize={12} fontWeight={500} color="#697586">
          {selectCompany?.length} Selected
        </Typography>
      }
      open={open}
      setOpen={setOpen}
      width={"340px"}
      maxHeight="calc(100vh - 340px)"
    >
      <Grid2 container spacing={1.5}>
        <Grid2 size={12}>
          <GKTextField
            size="small"
            type="search"
            onChange={handleSearchChange}
            placeholder="Search Company Name"
            slotProps={{
              input: {
                startAdornment: <SearchIcon />,
              },
            }}
          />
        </Grid2>
        <Show.When isTrue={searchText?.length === 0}>
          <Grid2 size={12}>
            <Typography
              fontSize={12}
              fontWeight={500}
              color="#697586"
              display={"flex"}
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              {formattedCompanyNames}
              {selectCompany?.length > 2 && (
                <Box
                  sx={{
                    borderRadius: 8,
                    background: " #E6F0F3",
                    p: "4px 8px",
                    color: " #006580",
                  }}
                >
                  +{selectCompany?.length - 2}
                </Box>
              )}
            </Typography>
          </Grid2>
        </Show.When>
        <Grid2
          size={12}
          sx={{
            maxHeight: "350px",
            overflowY: "auto",
            pr: 1,
            mt: 1,
          }}
          ref={listRef}
          onScroll={handleScroll}
        >
          {map(visibleCompanies, (company: CompaniesListInterface) => (
            <Grid2
              size={12}
              key={company.gridCode}
              sx={{
                ":hover": {
                  borderRadius: "6px",
                  backgroundColor: "#EEF5FA",
                },
              }}
            >
              <FormControlLabel
                control={
                  <GKCheckbox
                    checked={selectCompany.some(
                      (item) => item.value === company.value,
                    )}
                    onChange={() => {
                      const isSelected = selectCompany.some(
                        (item) => item.value === company.value,
                      );

                      const updatedOptions = isSelected
                        ? selectCompany.filter(
                            (item) => item.value !== company.value,
                          )
                        : [
                            ...selectCompany,
                            { ...company, weightage: "", limitValue: "" },
                          ];

                      setSelectCompany(updatedOptions);
                    }}
                  />
                }
                label={company.name}
                sx={{
                  "& .MuiTypography-root": {
                    fontSize: "13px",
                    fontWeight: 500,
                  },
                }}
              />
            </Grid2>
          ))}
        </Grid2>
        <Grid2 size={12}>
          <GKButton
            fullWidth
            variant="contained"
            startIcon={<CheckIcon />}
            onClick={() => {
              onSuccess(selectCompany);
              setOpen(false);
            }}
          >
            Submit
          </GKButton>
        </Grid2>
      </Grid2>
    </GKModal>
  );
};

export default CompanyModel;
